import { useState } from "react";
import DOMPurify from "dompurify";
import { AlertTriangle, CheckCircle2, Clock, FileDown, PenLine, Printer } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { SuccessPopup } from "@/components/ui/SuccessPopup";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { STATUS_LABELS, type DesignDocument, type DesignDocumentRevision, type ManagedFileBrief } from "../types";
import { useUploadSignedFile, useRejectRevision, useAddRevision, useAcknowledgeRevision } from "../api/documents";
import { useAuthStore } from "@/store/auth";
import { downloadFileTracked, FileDownloadStatusBadge } from "./FileDownloadStatus";
import type { DocumentationCluster } from "../types";

const STATUS_BADGE_COLOR: Record<string, "yellow" | "green" | "red"> = {
  pending_signature: "yellow",
  signed: "green",
  rejected: "red",
};

const STATUS_LEFT_BORDER: Record<string, string> = {
  pending_signature: "border-l-amber-400",
  signed: "border-l-green-400",
  rejected: "border-l-red-400",
};

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isHtmlEmpty(html: string): boolean {
  return !html || !html.replace(/<[^>]*>/g, "").trim();
}

function FileRow({ label, file }: { label: string; file: ManagedFileBrief | null }) {
  if (!file) return (
    <div className="flex items-center gap-1.5">
      <FileDown size={12} className="text-gray-300 flex-shrink-0" />
      <span className="text-xs text-gray-400 flex-shrink-0">{label}:</span>
      <span className="text-xs text-gray-300">—</span>
    </div>
  );
  const size = file.size_bytes ? ` (${formatBytes(file.size_bytes)})` : "";
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <FileDown size={12} className="text-gray-400 flex-shrink-0" />
      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{label}:</span>
      {file.url ? (
        <>
          <button
            type="button"
            onClick={() => downloadFileTracked(file.id, file.url!, file.original_filename)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate text-left"
          >
            {file.original_filename}{size}
          </button>
          <FileDownloadStatusBadge fileId={file.id} />
        </>
      ) : (
        <span className="text-xs text-gray-500 truncate">{file.original_filename}{size}</span>
      )}
    </div>
  );
}

function ReasonBox({ reason, authorName, createdAt }: { reason: string; authorName: string | null; createdAt: string }) {
  return (
    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-700/40 dark:bg-amber-900/15 overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-amber-200 dark:border-amber-700/40 bg-amber-100/60 dark:bg-amber-900/25 px-3 py-1.5">
        <AlertTriangle size={12} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
          Powód zmiany
        </span>
      </div>
      <div className="px-3 py-2.5">
        <div
          className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_strong]:font-semibold [&_em]:italic"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reason) }}
        />
        <p className="mt-2 text-[11px] text-amber-600/80 dark:text-amber-400/70">
          — {authorName ?? "nieznany"} &nbsp;·&nbsp; {fmt(createdAt)}
        </p>
      </div>
    </div>
  );
}

function RejectionBox({ revision }: { revision: DesignDocumentRevision }) {
  return (
    <div className="mb-3 rounded-md border border-red-200 bg-red-50 dark:border-red-800/40 dark:bg-red-900/15 overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-red-200 dark:border-red-800/40 bg-red-100/60 dark:bg-red-900/25 px-3 py-1.5">
        <AlertTriangle size={12} className="text-red-600 dark:text-red-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wide">
          Odrzucone
        </span>
      </div>
      <div className="px-3 py-2.5">
        {revision.rejection_reason && (
          <p className="text-xs text-red-800 dark:text-red-200 leading-relaxed whitespace-pre-wrap">
            {revision.rejection_reason}
          </p>
        )}
        <p className={`text-[11px] text-red-600/80 dark:text-red-400/70 ${revision.rejection_reason ? "mt-2" : ""}`}>
          — {revision.rejected_by_name ?? "nieznany"}
          {revision.rejected_at ? <> &nbsp;·&nbsp; {fmt(revision.rejected_at)}</> : null}
        </p>
      </div>
    </div>
  );
}

function RevisionTimeline({ createdAt, signedAt, isPaper }: { createdAt: string; signedAt: string | null; isPaper: boolean }) {
  if (isPaper) {
    return (
      <div className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-x-5 gap-y-1">
        <div className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
          <Printer size={11} className="flex-shrink-0" />
          <span>Sprawdzono papierowo: <span className="font-medium">{signedAt ? fmt(signedAt) : fmt(createdAt)}</span></span>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-x-5 gap-y-1">
      <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
        <Clock size={11} className="flex-shrink-0" />
        <span>Wysłano do sprawdzenia: <span className="font-medium text-gray-500 dark:text-gray-400">{fmt(createdAt)}</span></span>
      </div>
      {signedAt ? (
        <div className="flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400">
          <PenLine size={11} className="flex-shrink-0" />
          <span>Sprawdzono: <span className="font-medium">{fmt(signedAt)}</span></span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-[11px] text-gray-300 dark:text-gray-600">
          <PenLine size={11} className="flex-shrink-0" />
          <span>Oczekuje na sprawdzenie</span>
        </div>
      )}
    </div>
  );
}

function UploadSignedForm({ revisionId, clusterId, onDone }: { revisionId: string; clusterId: string; onDone: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const mutation = useUploadSignedFile(revisionId, clusterId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const file = files[0] ?? null;
    if (!file) { setError("Wybierz plik PDF lub ZIP."); return; }
    const ext = file.name.toLowerCase();
    if (!ext.endsWith(".pdf") && !ext.endsWith(".zip")) { setError("Akceptowane formaty: PDF, ZIP."); return; }
    if (file.size > 500 * 1024 * 1024) { setError("Plik jest za duży. Maksymalny rozmiar: 500 MB."); return; }
    try {
      await mutation.mutateAsync(file);
      // Confirm the upload succeeded before closing the form (requested by
      // reviewing designers who want a visible "file uploaded" acknowledgement).
      setShowSuccess(true);
    } catch {
      setError("Nie udało się przesłać pliku.");
    }
  }

  return (
    <>
    <SuccessPopup
      open={showSuccess}
      message="Udało się wgrać plik na system"
      onClose={() => {
        setShowSuccess(false);
        onDone();
      }}
    />
    <form onSubmit={handleSubmit} className="mt-3 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Wgraj sprawdzony plik</p>
      <FileDropZone
        files={files}
        onFilesChange={setFiles}
        accept=".pdf,.zip,application/pdf,application/zip"
      />
      <p className="text-xs text-gray-400 dark:text-gray-500">PDF lub ZIP (np. paczka podpisanych dokumentów), maks. 500 MB.</p>
      {error && <span className="text-xs text-red-500">{error}</span>}
      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending} className="text-xs py-1 px-3">
          {mutation.isPending ? "Przesyłanie…" : "Wyślij sprawdzony"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} className="text-xs py-1 px-3">Anuluj</Button>
      </div>
    </form>
    </>
  );
}

function AddRevisionForm({
  documentId,
  clusterId,
  isPaper,
  onDone,
}: {
  documentId: string;
  clusterId: string;
  isPaper: boolean;
  onDone: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useAddRevision(clusterId, documentId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const file = files[0] ?? null;
    if (!isPaper && !file) { setError("Wybierz plik PDF lub ZIP."); return; }
    if (file) {
      const ext = file.name.toLowerCase();
      if (!ext.endsWith(".pdf") && !ext.endsWith(".zip")) { setError("Akceptowane formaty: PDF, ZIP."); return; }
      if (file.size > 500 * 1024 * 1024) { setError("Plik jest za duży. Maksymalny rozmiar: 500 MB."); return; }
    }
    try {
      await mutation.mutateAsync({
        original_file: file ?? undefined,
        reason: isHtmlEmpty(reason) ? undefined : reason,
      });
      onDone();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string } } })?.response?.data?.errors;
      setError(typeof msg === "string" ? msg : "Nie udało się dodać rewizji.");
    }
  }

  return (
    <div className="mt-3 space-y-3 border-t pt-3 border-gray-100 dark:border-gray-700">
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
        {isPaper ? "Nowa wersja papierowa" : "Nowa rewizja"}
      </p>
      {!isPaper && (
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Powód zmiany <span className="text-gray-400">(wymagany od 2. wersji)</span>
          </label>
          <RichTextEditor
            value={reason}
            onChange={setReason}
            placeholder="Opisz dlaczego konieczna była zmiana dokumentu…"
          />
        </div>
      )}
      <div>
        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
          {isPaper
            ? "Skan sprawdzonego dokumentu (opcjonalnie)"
            : <>{`Nowy plik (PDF lub ZIP)`} <span className="text-red-500">*</span></>
          }
        </label>
        <FileDropZone
          files={files}
          onFilesChange={setFiles}
          accept=".pdf,.zip,application/pdf,application/zip"
        />
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Maksymalny rozmiar: 500 MB.
        </p>
      </div>
      {error && <span className="text-xs text-red-500">{error}</span>}
      <div className="flex gap-2">
        <Button type="submit" onClick={handleSubmit} disabled={mutation.isPending} className="text-xs py-1 px-3">
          {mutation.isPending ? "Przesyłanie…" : isPaper ? "Zapisz wersję" : "Wyślij nową wersję"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} className="text-xs py-1 px-3">Anuluj</Button>
      </div>
    </div>
  );
}

function AcknowledgeButton({ revision, clusterId }: { revision: DesignDocumentRevision; clusterId: string }) {
  const mutation = useAcknowledgeRevision(revision.id, clusterId);
  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40 transition-colors"
    >
      <CheckCircle2 size={13} />
      {mutation.isPending ? "Zapisywanie…" : "Oznacz jako przeczytane"}
    </button>
  );
}

interface Props {
  document: DesignDocument;
  cluster: DocumentationCluster;
  isManager: boolean;
}

export function RevisionHistory({ document, cluster, isManager }: Props) {
  const user = useAuthStore((s) => s.user);
  const isContractor = user?.role === "contractor";
  const isEmployeeSigner =
    !!user && !!cluster.employee_signer && String(cluster.employee_signer) === String(user.id);
  const isSigner = isContractor || isEmployeeSigner;
  const isPaper = cluster.signing_mode === "paper";
  const [uploadingRevisionId, setUploadingRevisionId] = useState<string | null>(null);
  const [addingRevision, setAddingRevision] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);
  const rejectMutation = useRejectRevision(
    document.revisions[document.revisions.length - 1]?.id ?? "",
    cluster.id,
  );

  const latestRevision: DesignDocumentRevision | undefined =
    document.revisions[document.revisions.length - 1];

  const canUploadSigned =
    !isPaper &&
    isSigner &&
    latestRevision?.status === "pending_signature" &&
    !latestRevision.signed_file;

  // The signer (designer / employee signer) may also refuse to sign — with an optional reason.
  const canReject =
    !isPaper &&
    (isManager || isSigner) &&
    latestRevision?.status === "pending_signature";

  async function handleReject() {
    setRejectError(null);
    try {
      await rejectMutation.mutateAsync({ reason: rejectReason.trim() || undefined });
      setRejecting(false);
      setRejectReason("");
    } catch {
      setRejectError("Nie udało się odrzucić rewizji.");
    }
  }

  const canAddRevision =
    isManager &&
    (latestRevision?.status === "signed" || latestRevision?.status === "rejected");

  return (
    <div className="mt-3">
      <div className="space-y-2">
        {document.revisions.map((rev) => {
          const isAuthor = rev.created_by !== null && String(rev.created_by) === String(user?.id);

          return (
            <div
              key={rev.id}
              className={`bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600 border-l-4 ${STATUS_LEFT_BORDER[rev.status] ?? "border-l-gray-300"} overflow-hidden`}
            >
              <div className="px-3 py-2.5">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2.5">
                  <div>
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                      Wersja {rev.revision_number}
                    </span>
                    {rev.created_by_name && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                        · {rev.created_by_name}
                      </span>
                    )}
                  </div>
                  <Badge color={STATUS_BADGE_COLOR[rev.status] ?? "yellow"}>
                    {STATUS_LABELS[rev.status]}
                  </Badge>
                </div>

                {/* Reason box — styled amber, HTML rendered, visible to all */}
                {rev.reason && !isHtmlEmpty(rev.reason) && (
                  <ReasonBox
                    reason={rev.reason}
                    authorName={rev.created_by_name}
                    createdAt={rev.created_at}
                  />
                )}

                {/* Rejection info — who rejected, when and (optionally) why */}
                {rev.status === "rejected" && (rev.rejected_by_name || rev.rejection_reason) && (
                  <RejectionBox revision={rev} />
                )}

                {/* Files */}
                <div className="space-y-1">
                  {isPaper ? (
                    <FileRow label="Skan" file={rev.original_file} />
                  ) : (
                    <>
                      <FileRow label="Do sprawdzenia" file={rev.original_file} />
                      <FileRow label="Sprawdzony" file={rev.signed_file} />
                    </>
                  )}
                </div>

                {/* Upload signed (electronic only) */}
                {canUploadSigned && rev.id === latestRevision?.id && (
                  uploadingRevisionId === rev.id ? (
                    <UploadSignedForm
                      revisionId={rev.id}
                      clusterId={cluster.id}
                      onDone={() => setUploadingRevisionId(null)}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setUploadingRevisionId(rev.id)}
                      className="mt-1.5 text-xs py-1 px-3"
                    >
                      Wgraj sprawdzony plik
                    </Button>
                  )
                )}

                {/* Timeline */}
                <RevisionTimeline
                  createdAt={rev.created_at}
                  signedAt={rev.signed_at}
                  isPaper={isPaper}
                />

                {/* Acknowledge — only for revision author (electronic clusters) */}
                {!isPaper && isAuthor && rev.status === "signed" && !rev.acknowledged_at && (
                  <AcknowledgeButton revision={rev} clusterId={cluster.id} />
                )}

                {/* Acknowledged confirmation — visible to everyone */}
                {rev.status === "signed" && rev.acknowledged_at && !isPaper && (
                  <div className="mt-2 flex items-center gap-1.5 rounded bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 px-2.5 py-1.5">
                    <CheckCircle2 size={13} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                      Odebrano {fmt(rev.acknowledged_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canReject && !rejecting && (
        <div className="mt-2">
          <Button
            type="button"
            variant="danger"
            onClick={() => { setRejecting(true); setRejectError(null); }}
            disabled={rejectMutation.isPending}
            className="text-xs py-1 px-3"
          >
            Odrzuć rewizję
          </Button>
        </div>
      )}

      {canReject && rejecting && (
        <div className="mt-2 space-y-2 rounded-md border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-900/10 px-3 py-2.5">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Powód odrzucenia <span className="text-gray-400">(opcjonalnie)</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={2000}
              autoFocus
              className="w-full border border-red-300 dark:border-red-800/60 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="np. Zakres niezgodny z uzgodnieniami — proszę o poprawioną wersję."
            />
          </div>
          {rejectError && <p className="text-xs text-red-600 dark:text-red-400">{rejectError}</p>}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="danger"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
              className="text-xs py-1 px-3"
            >
              {rejectMutation.isPending ? "Odrzucanie…" : "Odrzuć rewizję"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setRejecting(false); setRejectReason(""); setRejectError(null); }}
              disabled={rejectMutation.isPending}
              className="text-xs py-1 px-3"
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {canAddRevision && !addingRevision && (
        <div className="mt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setAddingRevision(true)}
            className="text-xs py-1 px-3"
          >
            Dodaj nową wersję dokumentu
          </Button>
        </div>
      )}

      {addingRevision && (
        <AddRevisionForm
          documentId={document.id}
          clusterId={cluster.id}
          isPaper={isPaper}
          onDone={() => setAddingRevision(false)}
        />
      )}
    </div>
  );
}
